import { Box, FormField, Input, Label, Legend, Radio, Switch, Text, Textarea } from '@zillow/constellation-10';
import { type ZillowAdsProps } from './BoostWidget.types';
import type React from 'react';
import { ZillowAdPreview } from '../ZillowAdPreview';
import { BoostAdFlagType } from '@zg-rentals/rental-manager-env';
import { BuildingMediaMessage } from './BuildingMediaMessage';
import { getDefaultRentStartingAt } from './BoostWidget';
import { FairHousingAlert } from './FairHousingAlert';
import { styled } from '@/styled-system/jsx';

const adFlagOptions = [
  BoostAdFlagType.NONE,
  BoostAdFlagType.GRAND_OPENING,
  BoostAdFlagType.CLOSE_TO_TRANSIT,
  BoostAdFlagType.SPECIAL_OFFER,
  BoostAdFlagType.PET_FRIENDLY,
  BoostAdFlagType.CUSTOM,
  BoostAdFlagType.NEWLY_RENOVATED,
];

const CustomFieldSet = styled(FormField, {
  base: {
    marginTop: '800',
  },
});

export const CapitalizeLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text css={{ textTransform: 'capitalize', marginLeft: '100' }}>{children}</Text>
);

export const ZillowAds = ({
  imageSrc,
  rentStartingAtPrice,
  title,
  onChangeFormValue,
  formValues,
  errors,
  dirtyFields,
  additionalBoostAdFlagOptions,
  disabled,
  displayMediaMessage,
  onClickSeeBuildingMedia,
  shouldHideAdFlagSelect,
  hideAdFlagOptions,
  displayFairHousingError,
}: ZillowAdsProps) => {
  const allFlagOptions = [...new Set(adFlagOptions.concat(additionalBoostAdFlagOptions))].filter(
    (opt) => !hideAdFlagOptions?.includes(opt),
  );

  return (
    <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', paddingInline: '400', paddingBlock: '500' }}>
      <Box css={{ gridColumn: { base: 'span 2', lg: 'span 1' }, paddingRight: { lg: '400' } }}>
        {displayFairHousingError && <FairHousingAlert />}
        {!shouldHideAdFlagSelect && (
          <FormField label={<Legend css={{ marginBottom: '400' }}>Ad flag</Legend>} optional>
            <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'sm' }}>
              {allFlagOptions.map((adFlagVal) => (
                <Box key={adFlagVal} css={{ display: 'flex', alignItems: 'center', gap: 'xs' }}>
                  <Radio
                    name="ad-flag"
                    value={adFlagVal}
                    checked={formValues.adFlag === adFlagVal}
                    onChange={() => onChangeFormValue('adFlag', adFlagVal)}
                  />
                  <CapitalizeLabel>{adFlagVal.replace(/_/g, ' ').toLocaleLowerCase()}</CapitalizeLabel>
                </Box>
              ))}
            </Box>
          </FormField>
        )}
        {shouldHideAdFlagSelect && <Box css={{ marginTop: '-400' }} />}
        {formValues.adFlag === BoostAdFlagType.CUSTOM && (
          <CustomFieldSet
            label={<Legend>Custom flag</Legend>}
            description={
              <Box>
                <Text textStyle="body-xs">15 characters allowed</Text>
                {dirtyFields.get('customFlag') && errors.get('customFlag') && (
                  <Text textStyle="body-xs" css={{ color: 'red.600' }}>
                    {errors.get('customFlag')}
                  </Text>
                )}
              </Box>
            }
            error={dirtyFields.get('customFlag') && Boolean(errors.get('customFlag'))}
            required
          >
            <Input
              placeholder="1 month free"
              value={formValues.customFlag}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeFormValue('customFlag', e.target.value)}
              disabled={disabled}
            />
          </CustomFieldSet>
        )}
        <CustomFieldSet
          label={<Legend>Subheader</Legend>}
          description={
            <Box>
              <Text textStyle="body-xs">35 characters allowed</Text>
              {dirtyFields.get('subheader') && errors.get('subheader') && (
                <Text textStyle="body-xs" css={{ color: 'red.600' }}>
                  {errors.get('subheader')}
                </Text>
              )}
            </Box>
          }
          error={dirtyFields.get('subheader') && Boolean(errors.get('subheader'))}
          optional
        >
          <Input
            placeholder="Outdoor park, Dog wash, Big windows"
            value={formValues.subheader}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeFormValue('subheader', e.target.value)}
            disabled={disabled}
          />
        </CustomFieldSet>
        <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(2, auto)', gap: 'md' }}>
          <CustomFieldSet
            label={
              <Legend
                css={{ marginBottom: '400' }}
              >{`Display "${getDefaultRentStartingAt(rentStartingAtPrice)}"`}</Legend>
            }
            description={
              <Text textStyle="body-xs" css={{ marginTop: '200', display: 'block' }}>
                Toggle off to add custom details instead
              </Text>
            }
          >
            <Box css={{ display: 'flex', alignItems: 'center', gap: 'xs' }}>
              <Switch
                checked={formValues.displayRentStartingAt}
                onChange={() => onChangeFormValue('displayRentStartingAt', !formValues.displayRentStartingAt)}
                disabled={disabled}
              />
              <Label>On</Label>
            </Box>
          </CustomFieldSet>
          {!formValues.displayRentStartingAt && (
            <CustomFieldSet
              label={<Legend>Custom details</Legend>}
              description={
                <Box>
                  <Text textStyle="body-xs">35 characters allowed</Text>
                  {dirtyFields.get('customDetails') && errors.get('customDetails') && (
                    <Text textStyle="body-xs" css={{ color: 'red.600' }}>
                      {errors.get('customDetails')}
                    </Text>
                  )}
                </Box>
              }
              error={dirtyFields.get('customDetails') && Boolean(errors.get('customDetails'))}
            >
              <Input
                placeholder="1 month free"
                value={formValues.customDetails}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onChangeFormValue('customDetails', e.target.value)
                }
                disabled={disabled}
              />
            </CustomFieldSet>
          )}
        </Box>
        <CustomFieldSet
          label={<Legend>Visit website URL</Legend>}
          description={
            <Box>
              {dirtyFields.get('websiteUrl') && errors.get('websiteUrl') && (
                <Text textStyle="body-xs" css={{ color: 'red.600' }}>
                  {errors.get('websiteUrl')}
                </Text>
              )}
            </Box>
          }
          error={dirtyFields.get('websiteUrl') && Boolean(errors.get('websiteUrl'))}
          required
        >
          <Textarea
            value={formValues.websiteUrl}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChangeFormValue('websiteUrl', e.target.value)}
            disabled={disabled}
          />
          <Text textStyle="body-xs" css={{ marginTop: 'xs' }}>
            The website url will be used in meta ads too
          </Text>
        </CustomFieldSet>
      </Box>
      <Box
        css={{
          gridColumn: { base: 'span 2', lg: 'span 1' },
          paddingLeft: { lg: '400' },
          marginTop: { base: '400', lg: 0 },
        }}
      >
        <Text textStyle="body-sm-bold" css={{ marginBottom: '200', display: 'block' }}>
          Preview of Sponsored ad on Zillow&apos;s search results page
        </Text>
        <ZillowAdPreview
          adFlag={formValues.adFlag}
          customFlag={formValues.customFlag}
          imageSrc={imageSrc}
          rentStartingAtPrice={rentStartingAtPrice}
          title={title}
          subheader={formValues.subheader}
          websiteLink={formValues.websiteUrl}
          displayRentStartingAt={formValues.displayRentStartingAt}
          customDetails={formValues.customDetails}
        />
        {displayMediaMessage && (
          <>
            <Box css={{ marginTop: '600' }} />
            <BuildingMediaMessage onClickSeeBuildingMedia={onClickSeeBuildingMedia} />
          </>
        )}
      </Box>
    </Box>
  );
};
